import {
  AnswerFieldsFragmentDoc,
  AssetViewFieldsFragmentDoc,
  CaseClusterFieldsFragmentDoc,
  ClientFieldsFragmentDoc,
  ExplicitUserAccessFieldsFragmentDoc,
  HostFieldsFieldsFragmentDoc,
  MasterClassFieldsFragmentDoc,
  MasterClassUserSessionFieldsFragmentDoc,
  MeetingFieldsFragmentDoc,
  MeetingStatsFieldsFragmentDoc,
  MessageFieldsFragmentDoc,
  NpsScoreFieldsFragmentDoc,
  SlideStatsFieldsFragmentDoc,
  TeamFieldsFragmentDoc,
  useOnCreateAnswerSubscription,
  useOnCreateAssetViewSubscription,
  useOnCreateCaseClusterSubscription,
  useOnCreateClientSubscription,
  useOnCreateExplicitUserAccessSubscription,
  useOnCreateHostFieldsSubscription,
  useOnCreateMasterClassSubscription,
  useOnCreateMasterClassUserSessionSubscription,
  useOnCreateMeetingStatsSubscription,
  useOnCreateMeetingSubscription,
  useOnCreateMessageSubscription,
  useOnCreateNpsScoreSubscription,
  useOnCreateSlideStatsSubscription,
  useOnCreateTeamSubscription,
  useOnCreateUserMeetingJoinSubscription,
  useOnDeleteAnswerSubscription,
  useOnDeleteAssetViewSubscription,
  useOnDeleteCaseClusterSubscription,
  useOnDeleteClientSubscription,
  useOnDeleteExplicitUserAccessSubscription,
  useOnDeleteHostFieldsSubscription,
  useOnDeleteMasterClassSubscription,
  useOnDeleteMasterClassUserSessionSubscription,
  useOnDeleteMeetingStatsSubscription,
  useOnDeleteMeetingSubscription,
  useOnDeleteMessageSubscription,
  useOnDeleteNpsScoreSubscription,
  useOnDeleteSlideStatsSubscription,
  useOnDeleteTeamSubscription,
  useOnDeleteUserMeetingJoinSubscription,
  useOnUpdateAnswerSubscription,
  useOnUpdateAssetViewSubscription,
  useOnUpdateCaseClusterSubscription,
  useOnUpdateClientSubscription,
  useOnUpdateExplicitUserAccessSubscription,
  useOnUpdateHostFieldsSubscription,
  useOnUpdateMasterClassSubscription,
  useOnUpdateMasterClassUserSessionSubscription,
  useOnUpdateMeetingStatsSubscription,
  useOnUpdateMeetingSubscription,
  useOnUpdateMessageSubscription,
  useOnUpdateNpsScoreSubscription,
  useOnUpdateSlideStatsSubscription,
  useOnUpdateTeamSubscription,
  UserMeetingJoinFieldsFragmentDoc,
} from '@/@generated/codegen/API';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';

const subscriptionMap = {
  Meeting: {
    onCreate: useOnCreateMeetingSubscription,
    onUpdate: useOnUpdateMeetingSubscription,
    onDelete: useOnDeleteMeetingSubscription,
    fragment: MeetingFieldsFragmentDoc,
  },
  MeetingStats: {
    onCreate: useOnCreateMeetingStatsSubscription,
    onUpdate: useOnUpdateMeetingStatsSubscription,
    onDelete: useOnDeleteMeetingStatsSubscription,
    fragment: MeetingStatsFieldsFragmentDoc,
  },
  Message: {
    onCreate: useOnCreateMessageSubscription,
    onUpdate: useOnUpdateMessageSubscription,
    onDelete: useOnDeleteMessageSubscription,
    fragment: MessageFieldsFragmentDoc,
  },
  Answer: {
    onCreate: useOnCreateAnswerSubscription,
    onUpdate: useOnUpdateAnswerSubscription,
    onDelete: useOnDeleteAnswerSubscription,
    fragment: AnswerFieldsFragmentDoc,
  },
  NPSScore: {
    onCreate: useOnCreateNpsScoreSubscription,
    onUpdate: useOnUpdateNpsScoreSubscription,
    onDelete: useOnDeleteNpsScoreSubscription,
    fragment: NpsScoreFieldsFragmentDoc,
  },
  SlideStats: {
    onCreate: useOnCreateSlideStatsSubscription,
    onUpdate: useOnUpdateSlideStatsSubscription,
    onDelete: useOnDeleteSlideStatsSubscription,
    fragment: SlideStatsFieldsFragmentDoc,
  },
  AssetViews: {
    onCreate: useOnCreateAssetViewSubscription,
    onUpdate: useOnUpdateAssetViewSubscription,
    onDelete: useOnDeleteAssetViewSubscription,
    fragment: AssetViewFieldsFragmentDoc,
  },
  HostFields: {
    onCreate: useOnCreateHostFieldsSubscription,
    onUpdate: useOnUpdateHostFieldsSubscription,
    onDelete: useOnDeleteHostFieldsSubscription,
    fragment: HostFieldsFieldsFragmentDoc,
  },
  MasterClass: {
    onCreate: useOnCreateMasterClassSubscription,
    onUpdate: useOnUpdateMasterClassSubscription,
    onDelete: useOnDeleteMasterClassSubscription,
    fragment: MasterClassFieldsFragmentDoc,
  },
  MasterClassUserSession: {
    onCreate: useOnCreateMasterClassUserSessionSubscription,
    onUpdate: useOnUpdateMasterClassUserSessionSubscription,
    onDelete: useOnDeleteMasterClassUserSessionSubscription,
    fragment: MasterClassUserSessionFieldsFragmentDoc,
  },
  UserMeetingJoin: {
    onCreate: useOnCreateUserMeetingJoinSubscription,
    onUpdate: null,
    onDelete: useOnDeleteUserMeetingJoinSubscription,
    fragment: UserMeetingJoinFieldsFragmentDoc,
  },
  Team: {
    onCreate: useOnCreateTeamSubscription,
    onUpdate: useOnUpdateTeamSubscription,
    onDelete: useOnDeleteTeamSubscription,
    fragment: TeamFieldsFragmentDoc,
  },
  Client: {
    onCreate: useOnCreateClientSubscription,
    onUpdate: useOnUpdateClientSubscription,
    onDelete: useOnDeleteClientSubscription,
    fragment: ClientFieldsFragmentDoc,
  },
  CaseCluster: {
    onCreate: useOnCreateCaseClusterSubscription,
    onUpdate: useOnUpdateCaseClusterSubscription,
    onDelete: useOnDeleteCaseClusterSubscription,
    fragment: CaseClusterFieldsFragmentDoc,
  },
  ExplicitUserAccess: {
    onCreate: useOnCreateExplicitUserAccessSubscription,
    onUpdate: useOnUpdateExplicitUserAccessSubscription,
    onDelete: useOnDeleteExplicitUserAccessSubscription,
    fragment: ExplicitUserAccessFieldsFragmentDoc,
  },
} as const;

type SubscriptionMap = typeof subscriptionMap;
type ModelName = keyof SubscriptionMap;
type SubscriptionType = 'onCreate' | 'onUpdate' | 'onDelete';

type HookFor<M extends ModelName, T extends SubscriptionType> = NonNullable<SubscriptionMap[M][T]>;
type HookOptions<M extends ModelName, T extends SubscriptionType> = Parameters<HookFor<M, T>>[0];

type UseEntitySubscriptionOptions<M extends ModelName> = Partial<{
  [K in SubscriptionType]: HookOptions<M, K>;
}>;

// TODO: I would like to avoid all these any casts in this file
export function useEntitySubscription<M extends ModelName>(
  modelName: M,
  options: UseEntitySubscriptionOptions<M>
): void {
  const { apolloClient } = useApolloClientContext();
  const subs = subscriptionMap[modelName];

  // onCreate
  if (options.onCreate) {
    const hook = subs.onCreate as HookFor<M, 'onCreate'>;
    hook(options.onCreate as any);
  }

  // onUpdate
  if (options.onUpdate) {
    const hook = subs.onUpdate as HookFor<M, 'onUpdate'>;
    hook(options.onUpdate as any);
  }

  // onDelete
  if (options.onDelete) {
    const { onData, ...rest } = options.onDelete as any;

    subs.onDelete({
      ...rest,
      onData: (res: any) => {
        const obj: any = Object.values(res.data ?? {})[0];
        if (obj?.id) {
          const cacheId = apolloClient.cache.identify({
            __typename: modelName,
            id: obj.id,
          });

          if (cacheId) {
            apolloClient.cache.evict({ id: cacheId });
            apolloClient.cache.gc();
          }
        }

        onData?.(res);
      },
    });
  }
}
